import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon,
} from "@heroicons/react/24/outline";
import { bladesApi } from "../../api/blades";
import { useLookups } from "../../hooks/useLookups";
import { Card, CardBody } from "../../components/ui/Card";
import Badge from "../../components/ui/Badge";
import TopBar from "../../components/layout/TopBar";

const TOPOLOGIES = ["RoundEdge", "DoveTail"];
const TOPO_LABEL = { RoundEdge: "Round Edge", DoveTail: "Dove Tail" };
const RELEASE_OPTIONS = ["All", "Released", "Draft"];

function useLocalStorage(key, fallback) {
  const [val, setVal] = useState(() => {
    try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
  });
  const set = (v) => { setVal(v); try { localStorage.setItem(key, v); } catch {} };
  return [val, set];
}

export default function ComponentLibraryPage() {
  const navigate = useNavigate();
  const { types: mecoTypes, labelFor: labelForMeco } = useLookups("meco_type");
  const { types: machineTypes } = useLookups("machine_type");
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [searchName, setSearchName] = useState("");
  const [searchHyco, setSearchHyco] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [typeFilter, setTypeFilter] = useState([]);       // [] = All
  const [topoFilter, setTopoFilter] = useState("All");
  const [releaseFilter, setReleaseFilter] = useState("All");
  const [projectTypeFilter, setProjectTypeFilter] = useState([]);  // [] = All

  const toggleType = (t) =>
    setTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const toggleProjectType = (t) =>
    setProjectTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const [view, setView] = useLocalStorage("library_view", "list");

  useEffect(() => {
    bladesApi.list()
      .then(r => setDesigns(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = designs.filter(d => {
    const name = (d.name || "").toLowerCase();
    const user = (d.owner_full_name || d.owner_username || "").toLowerCase();
    const hyco = (d.hyco_id || "").toLowerCase();
    if (searchName && !name.includes(searchName.toLowerCase())) return false;
    if (searchHyco && !hyco.includes(searchHyco.toLowerCase())) return false;
    if (searchUser && !user.includes(searchUser.toLowerCase())) return false;
    if (typeFilter.length > 0 && !typeFilter.includes(d.meco_type_id)) return false;
    if (topoFilter !== "All" && d.topology !== topoFilter) return false;
    if (releaseFilter === "Released" && !d.is_released) return false;
    if (releaseFilter === "Draft" && d.is_released) return false;
    if (projectTypeFilter.length > 0 && !projectTypeFilter.includes(d.project_machine_type_id)) return false;
    return true;
  });

  if (loading) return <div className="p-6 text-gray-500">Loading library…</div>;

  return (
    <>
      <TopBar title="Designs" />
      <main className="flex-1 overflow-y-auto p-6">
        <p className="text-gray-500 text-sm mb-5">All blade designs across your projects.</p>

        {msg && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-green-700 text-sm mb-4 flex items-center justify-between">
            <span>{msg}</span>
            <button onClick={() => setMsg(null)} className="text-green-500 hover:text-green-700 ml-3">×</button>
          </div>
        )}

        {/* ── Toolbar ── */}
        <div className="mb-5 space-y-2">
          {/* Row 1: search + view toggle */}
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input type="text" value={searchName} onChange={e => setSearchName(e.target.value)}
                placeholder="Search by design name…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500" />
            </div>
            <div className="relative w-44">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input type="text" value={searchHyco} onChange={e => setSearchHyco(e.target.value)}
                placeholder="HYCO number…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-bh-500" />
            </div>
            <div className="relative w-40">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input type="text" value={searchUser} onChange={e => setSearchUser(e.target.value)}
                placeholder="Filter by user…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500" />
            </div>
            <div className="flex rounded-md border border-gray-300 overflow-hidden">
              <button onClick={() => setView("grid")} title="Card view"
                className={`p-2 transition-colors ${view === "grid" ? "bg-bh-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button onClick={() => setView("list")} title="List view"
                className={`p-2 border-l border-gray-300 transition-colors ${view === "list" ? "bg-bh-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                <ListBulletIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Row 2: filter chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-400 mr-0.5">Component type:</span>
            <button onClick={() => setTypeFilter([])}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                ${typeFilter.length === 0 ? "bg-bh-600 text-white border-bh-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
              All
            </button>
            {mecoTypes.map(t => (
              <button key={t.id} onClick={() => toggleType(t.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                  ${typeFilter.includes(t.id) ? "bg-bh-600 text-white border-bh-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                {t.label}
              </button>
            ))}
            <span className="text-xs text-gray-400 ml-3 mr-0.5">Topology:</span>
            {["All", ...TOPOLOGIES].map(t => (
              <button key={t} onClick={() => setTopoFilter(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                  ${topoFilter === t ? "bg-bh-600 text-white border-bh-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                {t === "All" ? "All" : TOPO_LABEL[t]}
              </button>
            ))}
            <span className="text-xs text-gray-400 ml-3 mr-0.5">Status:</span>
            {RELEASE_OPTIONS.map(t => (
              <button key={t} onClick={() => setReleaseFilter(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                  ${releaseFilter === t ? "bg-bh-600 text-white border-bh-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                {t}
              </button>
            ))}
          </div>
          {/* Row 3: project type chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-400 mr-0.5">Project type:</span>
            <button onClick={() => setProjectTypeFilter([])}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                ${projectTypeFilter.length === 0 ? "bg-bh-600 text-white border-bh-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
              All
            </button>
            {machineTypes.map(t => (
              <button key={t.id} onClick={() => toggleProjectType(t.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                  ${projectTypeFilter.includes(t.id) ? "bg-bh-600 text-white border-bh-600" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        {filtered.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-gray-500 text-sm text-center py-8">
                {designs.length === 0
                  ? "No designs yet. Create a blade design inside a project to see it here."
                  : "No designs match your filters."}
              </p>
            </CardBody>
          </Card>

        ) : view === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map(d => (
              <Card key={d.id}
                className="cursor-pointer hover:border-bh-300 transition-colors"
                onClick={() => navigate(`/projects/${d.project_id}/blade/${d.id}/summary`)}>
                <CardBody>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2 mb-1">
                        <p className="text-gray-900 font-semibold truncate">{d.name}</p>
                        {d.hyco_id && <span className="text-xs font-mono text-gray-400 shrink-0">{d.hyco_id}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        {d.is_released
                          ? <Badge color="yellow">Released</Badge>
                          : <Badge color="gray">Draft</Badge>}
                        <Badge color="blue">{labelForMeco(d.meco_type_id)}</Badge>
                        <Badge color="gray">{TOPO_LABEL[d.topology] || d.topology}</Badge>
                      </div>
                      <div className="flex gap-2 text-xs text-gray-500 mb-1">
                        <span>{d.project_name}</span>
                        <span>·</span>
                        <span>{d.owner_full_name || d.owner_username}</span>
                      </div>
                      {d.derived_from?.hyco_id && (
                        <p className="text-xs text-gray-400 mb-1">
                          from{" "}
                          <Link
                            to={`/projects/${d.derived_from.project_id}/blade/${d.derived_from.id}/summary`}
                            onClick={e => e.stopPropagation()}
                            className="font-mono text-bh-600 hover:text-bh-700 hover:underline"
                          >
                            {d.derived_from.hyco_id}
                          </Link>
                        </p>
                      )}
                      {d.description && <p className="text-gray-400 text-xs truncate">{d.description}</p>}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-400 mt-1">
                        {d.rpm && <span>{d.rpm} rpm</span>}
                        {d.flow_rate && <span>Q={d.flow_rate} m³/s</span>}
                        {d.head && <span>H={d.head} m</span>}
                        {d.nq && <span>nq={d.nq?.toFixed(1)}</span>}
                        {d.d2 && <span>D₂={Math.round(d.d2 * 1000)} mm</span>}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-mono text-gray-400 uppercase tracking-wide w-36 hidden sm:table-cell">HYCO</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Status</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Type</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Topology</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Project</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Owner</th>
                  <th className="px-4 py-2.5 text-left text-xs font-mono text-gray-400 uppercase tracking-wide w-36 hidden xl:table-cell">Derived from</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(d => (
                  <tr key={d.id}
                    className="hover:bg-bh-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/projects/${d.project_id}/blade/${d.id}/summary`)}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {d.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400 hidden sm:table-cell">
                      {d.hyco_id || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {d.is_released
                        ? <Badge color="yellow">Released</Badge>
                        : <Badge color="gray">Draft</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color="blue">{labelForMeco(d.meco_type_id)}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge color="gray">{TOPO_LABEL[d.topology] || d.topology}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{d.project_name}</td>
                    <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">
                      {d.owner_full_name || d.owner_username}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      {d.derived_from?.hyco_id ? (
                        <Link
                          to={`/projects/${d.derived_from.project_id}/blade/${d.derived_from.id}/summary`}
                          onClick={e => e.stopPropagation()}
                          className="font-mono text-xs text-bh-600 hover:text-bh-700 hover:underline"
                        >
                          {d.derived_from.hyco_id}
                        </Link>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

    </>
  );
}
